// @flow

import React from 'react';
import ReactDOM from 'react-dom';
import createFocusTrap, { type FocusTrap } from 'focus-trap';
import styled from 'styled-components';

import KeyConstants from '../utils/keycode';
import Icon from './Icons';
import Button from './Button';

import typeof TTheme from '../theme';

const { ESC, ENTER } = KeyConstants;

type TP = { theme: TTheme };

const Backdrop = styled.div`
  align-items: flex-start;
  background: rgba(0, 0, 0, 0.6);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
`;

const Exit = styled(Icon)`
  color: ${({ theme }: TP) => theme.colors.blueyGrey};
  cursor: pointer;
  height: 20px;
  position: absolute;
  right: 10px;
  top: 10px;
  width: 20px;
  &:focus {
    color: ${({ theme }: TP) => theme.colors.primary};
  }
`;

const Container = styled.div`
  background-color: ${({ theme }: TP) => theme.colors.white};
  border-radius: 3px;
  border: solid 1px ${({ theme }: TP) => theme.colors.blueyGrey};
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin-top: 10%;
  max-width: 90%;
  outline: none;
  padding: ${({ theme }: TP) =>
    `${theme.spacing.tiny} 95px ${theme.spacing.standard}`};
  position: relative;
  text-align: center;
  width: 540px;
  @media ${({ theme }: TP) => theme.mediaQueries.small} {
    padding: ${({ theme }: TP) =>
      `${theme.spacing.tiny} 10% ${theme.spacing.standard}`};
  }
`;

export const DialogButton = styled(Button)`
  margin: ${({ variant, theme }: TP & { variant?: string }) =>
      variant ? theme.spacing.standard : 0}
    auto 0;
  width: 200px;
`;

type DialogProps = {
  onClose?: Function,
  children: any,
  onClose: Function,
};

const stopPropagation = (e: Object) => e.stopPropagation();

class Dialog extends React.Component<DialogProps, void> {
  fg: FocusTrap | null;
  container: Element;

  componentWillMount() {
    const exists = document.getElementById('__dialog');
    if (!exists) {
      const el = document.createElement('div');
      el.id = '__dialog';
      // $FlowIgnore document.body is non-null
      document.body.appendChild(el);
      this.container = el;
    } else {
      this.container = exists;
    }
  }

  componentDidMount() {
    try {
      this.fg = createFocusTrap(this.container);
      this.fg.activate();
    } catch (err) {
      // ignore createFocusTrap() failure
    }
  }

  componentWillUnmount() {
    if (this.fg !== null) {
      this.fg.deactivate();
    }
    // $FlowIgnore: break type contract. it’s okay since we are unmounting and won’t call any more code that uses it
    this.container = null;
  }

  handleKeyDown = (event: SyntheticEvent<HTMLElement>) => {
    if (event.which === ESC) {
      this.props.onClose();
    }
  };

  handleExitKeyDown = (event: SyntheticEvent<HTMLElement>) => {
    if (event.which === ENTER) {
      this.props.onClose();
    }
  };

  render() {
    return ReactDOM.createPortal(
      <Backdrop onKeyDown={this.handleKeyDown}>
        <Container
          tabIndex={'-1'}
          onClick={stopPropagation}
          onKeyDown={this.handleKeyDown}>
          {this.props.children}
          <Exit
            tabIndex={0}
            type="clear"
            onKeyDown={this.handleExitKeyDown}
            onClick={this.props.onClose}
          />
        </Container>
      </Backdrop>,
      this.container
    );
  }
}

export default Dialog;



// WEBPACK FOOTER //
// ./src/components/Dialog.js